import {
  ArchiveImportResponseSchema,
  type CheckImportRequest,
  IMPORT_OUTCOMES,
  type ImportCounts,
  ImportCheckSchema,
  type ImportOutcome,
  ImportPreviewSchema,
  type StartImportRequest,
  StartImportResponseSchema,
} from '@fundline/shared/schemas/imports';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { workerApi, workerDownload, workerUpload } from '../../lib/worker-api.ts';

/**
 * CSV imports (M8). Admins only: lists and progress come from Supabase (RLS: admins read), every
 * action goes through the worker, which re-checks the role and does the work.
 */

export interface ImportSummary {
  id: string;
  fileName: string;
  status: 'uploaded' | 'mapped' | 'running' | 'completed' | 'failed';
  totalRows: number;
  processedRows: number;
  counts: ImportCounts;
  needsState: number;
  warnings: number;
  lookupCount: number;
  lookupCostCents: string;
  archivedCount: number;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
  createdBy: string;
  sourceId: string | null;
  consentSource: string | null;
  consentSourceDetail: string | null;
  consentScope: string | null;
  consentOnOrBefore: string | null;
}

const COLUMNS =
  'id, file_name, status, total_rows, processed_rows, created_count, updated_count, skipped_duplicate, skipped_existing, skipped_archived, skipped_landline, skipped_invalid, skipped_outside_us, skipped_suppressed, skipped_vendor_dnc, skipped_no_consent, needs_state_count, warning_count, lookup_count, lookup_cost_cents, archived_count, error, created_at, completed_at, created_by, source_id, consent_source, consent_source_detail, consent_scope, consent_on_or_before';

type Row = {
  id: string;
  file_name: string;
  status: ImportSummary['status'];
  total_rows: number;
  processed_rows: number;
  created_count: number;
  updated_count: number;
  skipped_duplicate: number;
  skipped_existing: number;
  skipped_archived: number;
  skipped_landline: number;
  skipped_invalid: number;
  skipped_outside_us: number;
  skipped_suppressed: number;
  skipped_vendor_dnc: number;
  skipped_no_consent: number;
  needs_state_count: number;
  warning_count: number;
  lookup_count: number;
  lookup_cost_cents: number | string;
  archived_count: number;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  created_by: string;
  source_id: string | null;
  consent_source: string | null;
  consent_source_detail: string | null;
  consent_scope: string | null;
  consent_on_or_before: string | null;
};

function toSummary(r: Row): ImportSummary {
  return {
    id: r.id,
    fileName: r.file_name,
    status: r.status,
    totalRows: r.total_rows,
    processedRows: r.processed_rows,
    counts: {
      created: r.created_count,
      updated: r.updated_count,
      skipped_duplicate: r.skipped_duplicate,
      skipped_existing: r.skipped_existing,
      skipped_archived: r.skipped_archived,
      skipped_landline: r.skipped_landline,
      skipped_invalid: r.skipped_invalid,
      skipped_outside_us: r.skipped_outside_us,
      skipped_suppressed: r.skipped_suppressed,
      skipped_vendor_dnc: r.skipped_vendor_dnc,
      skipped_no_consent: r.skipped_no_consent,
    },
    needsState: r.needs_state_count,
    warnings: r.warning_count,
    lookupCount: r.lookup_count,
    lookupCostCents: String(r.lookup_cost_cents),
    archivedCount: r.archived_count,
    error: r.error,
    createdAt: r.created_at,
    completedAt: r.completed_at,
    createdBy: r.created_by,
    sourceId: r.source_id,
    consentSource: r.consent_source,
    consentSourceDetail: r.consent_source_detail,
    consentScope: r.consent_scope,
    consentOnOrBefore: r.consent_on_or_before,
  };
}

export function useImportsQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.imports(workspaceId),
    queryFn: async (): Promise<ImportSummary[]> => {
      const { data, error } = await supabase()
        .from('imports')
        .select(COLUMNS)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw new Error(`Couldn't load imports: ${error.message}`);
      return (data as Row[]).map(toSummary);
    },
  });
}

/** One import; polled every 2 seconds while it runs. */
export function useImportQuery(workspaceId: string, importId: string) {
  return useQuery({
    queryKey: queryKeys.importDetail(workspaceId, importId),
    queryFn: async (): Promise<ImportSummary> => {
      const { data, error } = await supabase()
        .from('imports')
        .select(COLUMNS)
        .eq('workspace_id', workspaceId)
        .eq('id', importId)
        .single();
      if (error) throw new Error(`Couldn't load the import: ${error.message}`);
      return toSummary(data);
    },
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  });
}

export function useImportPreviewQuery(workspaceId: string, importId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.importPreview(workspaceId, importId),
    queryFn: () =>
      workerApi(`/workspaces/${workspaceId}/imports/${importId}`, { schema: ImportPreviewSchema }),
    enabled,
    staleTime: Infinity,
  });
}

export interface ImportIssue {
  row: number;
  outcome: ImportOutcome;
  reason: string | null;
  phone: string | null;
  warnings: string[];
}

/** The first rows that were skipped, warned or need a state (the report has them all). */
export function useImportIssuesQuery(workspaceId: string, importId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.importIssues(workspaceId, importId),
    enabled,
    queryFn: async (): Promise<ImportIssue[]> => {
      const { data, error } = await supabase()
        .from('import_rows')
        .select('row_number, outcome, reason, phone, warnings')
        .eq('workspace_id', workspaceId)
        .eq('import_id', importId)
        .eq('in_report', true)
        .order('row_number')
        .limit(100);
      if (error) throw new Error(`Couldn't load the rows: ${error.message}`);
      return data.map((r) => ({
        row: r.row_number + 1,
        outcome: (IMPORT_OUTCOMES as readonly string[]).includes(r.outcome)
          ? (r.outcome as ImportOutcome)
          : 'skipped_invalid',
        reason: r.reason,
        phone: r.phone,
        warnings: Array.isArray(r.warnings)
          ? r.warnings.filter((w): w is string => typeof w === 'string')
          : [],
      }));
    },
  });
}

export function useUploadImport(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, sourceId }: { file: File; sourceId: string | null }) =>
      workerUpload(
        `/workspaces/${workspaceId}/imports${sourceId ? `?sourceId=${sourceId}` : ''}`,
        file,
        ImportPreviewSchema,
      ),
    onSuccess: async (preview) => {
      queryClient.setQueryData(queryKeys.importPreview(workspaceId, preview.importId), preview);
      await queryClient.invalidateQueries({ queryKey: queryKeys.imports(workspaceId) });
    },
  });
}

export function useCheckImport(workspaceId: string, importId: string) {
  return useMutation({
    mutationFn: (body: CheckImportRequest) =>
      workerApi(`/workspaces/${workspaceId}/imports/${importId}/check`, {
        method: 'POST',
        body,
        schema: ImportCheckSchema,
      }),
  });
}

export function useStartImport(workspaceId: string, importId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StartImportRequest) =>
      workerApi(`/workspaces/${workspaceId}/imports/${importId}/start`, {
        method: 'POST',
        body,
        schema: StartImportResponseSchema,
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.imports(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.intakeSources(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tags(workspaceId) }),
      ]),
  });
}

export function useArchiveImported(workspaceId: string, importId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      workerApi(`/workspaces/${workspaceId}/imports/${importId}/archive-created`, {
        method: 'POST',
        schema: ArchiveImportResponseSchema,
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.imports(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) }),
      ]),
  });
}

export function downloadImportReport(workspaceId: string, importId: string) {
  return workerDownload(`/workspaces/${workspaceId}/imports/${importId}/report`, 'report.csv');
}

export function downloadImportFile(workspaceId: string, importId: string) {
  return workerDownload(`/workspaces/${workspaceId}/imports/${importId}/file`, 'import.csv');
}
